"use client";

import { ChartData, ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import { IBookEquityGraphProps } from "../types";
import { chartJsGridColor, chartJsTextColor } from "@portal/helpers/chartJs";
import { nb } from "date-fns/locale";

/**
 * Chart.js version of the unlisted securities book equity history chart.
 * @param interface
 * @returns a chart.
 */
const BookEquityChart = ({ equityList, tradeHistory }: IBookEquityGraphProps) => {
  const datasets: ChartData<"line">["datasets"] = [];

  if (equityList) {
    datasets.push({
      label: "Bokført per ek-bevis",
      borderColor: "#0066ff",
      backgroundColor: "#0066ff",
      borderWidth: 2,
      tension: 0,
      data: equityList.map(e => ({ x: new Date(e.date).getTime(), y: e.bookEquityPerShare })),
    });
  }

  if (tradeHistory) {
    datasets.push({
      label: "Kurs",
      borderColor: "#507d03",
      backgroundColor: "#507d03",
      borderWidth: 2,
      tension: 0,
      data: tradeHistory.map(t => ({ x: new Date(t.date).getTime(), y: t.close })),
    });
  }

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    elements: { point: { radius: 0, hoverRadius: 4 } },
    scales: {
      x: {
        type: "time",
        adapters: { date: { locale: nb } },
        time: { tooltipFormat: "dd.MM.yyyy", displayFormats: { day: "d. MMM", month: "MMM ''yy", year: "yyyy" } },
        ticks: { color: chartJsTextColor(), maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
        grid: { display: false },
      },
      y: { ticks: { color: chartJsTextColor() }, grid: { color: chartJsGridColor() } },
    },
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: { color: chartJsTextColor(), boxWidth: 12, usePointStyle: true, pointStyle: "line" },
      },
    },
  };

  return (
    <div style={{ height: 500 }}>
      <Line
        data={{ datasets }}
        options={options}
        plugins={[
          {
            id: "increase-legend-spacing",
            beforeInit(chart) {
              // Check if legend is enabled
              if (!chart.legend) return;

              // Get a reference to the native fit function
              const originalFit = chart.legend.fit;

              // Override the fit function to append extra pixels
              chart.legend.fit = function fit() {
                // Run native logic first to establish dimensions
                originalFit.bind(chart.legend)();

                // Add 30px of extra padding/margin beneath or next to the legend
                this.height += 15;
              };
            },
          },
        ]}
      />
    </div>
  );
};

export default BookEquityChart;
